import { Autocomplete, CircularProgress, MenuItem, TextField } from '@mui/material'
import React from 'react'
import { com } from '@eidu/form'
import DraftFieldValue from '../../domain/entity/DraftFieldValue'
import EqualityHashMap from '../../util/EqualityHashMap'
import EntityReference from '../data/EntityReference'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import useEntityById from '../../io/useEntityById'
import Field = com.eidu.sharedlib.entity.field.Field
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityId = com.eidu.sharedlib.entity.EntityId
import entityIdFromStringOrNull = com.eidu.sharedlib.entity.entityIdFromStringOrNull
import entityIdFromString = com.eidu.sharedlib.entity.entityIdFromString

export type EntityFormReferenceInputProps = {
  field: Field
  fieldValue: DraftFieldValue | undefined
  referenceableEntityTypesById: ReadonlyMap<EntityTypeId, EntityType | null>
  referenceableEntitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>
  updateField: (value: DraftFieldValue) => void
  error: boolean
  setSearchQuery: (query: string) => void
  loadMore?: () => void
  hasMore?: boolean
  loading?: boolean
}

const EntityFormReferenceInput = ({
  field,
  fieldValue,
  referenceableEntitiesByTypeId,
  referenceableEntityTypesById,
  updateField,
  error,
  setSearchQuery,
  loadMore,
  hasMore = false,
  loading = false,
}: EntityFormReferenceInputProps) => {
  const getEntityId = (value: DraftFieldValue | undefined) =>
    value?.value?.let((it) => entityIdFromStringOrNull(it) ?? undefined)
  const selectedId = getEntityId(fieldValue)

  const entitiesById = new EqualityHashMap(
    (field.validReferenceTypes
      ? [...referenceableEntitiesByTypeId.entries()].flatMap(([typeId, entities]) =>
          field.validReferenceTypes?.asJsReadonlySetView()?.has(typeId) ? [entities] : []
        )
      : [...referenceableEntitiesByTypeId.values()]
    ).flatMap((entityById) => [...entityById.entries()])
  )

  const { entity: selectedEntity } = useEntityById(selectedId ?? undefined)

  const extraEntries: [EntityId, EntityWithLabelAndRelated][] = selectedEntity
    ? [[selectedEntity.entity.id, selectedEntity]]
    : []
  const referenceableEntitiesById = new EqualityHashMap<EntityId, EntityWithLabelAndRelated>([
    ...Array.from(entitiesById.entries()),
    ...extraEntries,
  ])

  const optionsBase = [...referenceableEntitiesById.keys()].map((it) => it.toString())
  const options = selectedId
    ? [selectedId.toString(), ...optionsBase.filter((id) => id !== selectedId.toString())]
    : optionsBase

  return (
    <Autocomplete
      key={`field-${field.id.toString()}`}
      disablePortal
      disabled={false}
      options={options}
      getOptionLabel={(entityId) => {
        const entity = referenceableEntitiesById.get(entityIdFromString(entityId))
        return entity?.primaryLabelText ?? ''
      }}
      loading={loading}
      value={selectedId?.toString() || null}
      onChange={(_, value) => updateField({ fieldId: field.id, type: field.type, value: value ?? '' })}
      noOptionsText="No entities"
      onInputChange={(_, newInputValue) => {
        setSearchQuery(newInputValue)
      }}
      ListboxProps={{
        onScroll: (event: React.UIEvent<HTMLUListElement>) => {
          const list = event.currentTarget
          if (hasMore && loadMore) {
            const nearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 24
            if (nearBottom) loadMore()
          }
        },
        sx: {
          minWidth: 'fit-content',
          maxWidth: '80vw',
        },
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 'fit-content',
            maxWidth: '80vw',
          },
        },
      }}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          required={field.required}
          label={field.name}
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={30} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, entityId) => {
        const entity = referenceableEntitiesById.get(entityIdFromString(entityId))
        const referencedEntityType = entity && referenceableEntityTypesById.get(entity.entity.typeId)
        return (
          entity && (
            <MenuItem {...props} key={`menu-item-${entity.entity.id.toString()}`}>
              <EntityReference
                id={entityIdFromString(entityId)}
                typeId={entity.entity.typeId}
                label={entity.primaryLabelText}
                secondaryLabel={entity.secondaryLabelText}
                labelAsLink={false}
                typeName={referencedEntityType?.name}
              />
            </MenuItem>
          )
        )
      }}
    />
  )
}

export default EntityFormReferenceInput
